import { GetMyPermissionsDocument } from '../graphql/generated/graphql';
import { gqlRequest } from '../lib/graphql-client';
import type { Action, FrontendRule, Subject } from '../types/ability';

// Runtime drift guard. `satisfies` keeps these lists in sync with the type
// unions in ability.ts — if Action or Subject changes, TypeScript fails here
// first. Folded into a Set for O(1) lookup during the per-rule check below.
const KNOWN_ACTIONS = new Set<string>([
  'manage',
  'create',
  'read',
  'update',
  'delete',
  'send',
  'assign',
] satisfies Action[]);
const KNOWN_SUBJECTS = new Set<string>([
  'Provider',
  'Staff',
  'Member',
  'Payment',
  'PaymentReceipt',
  'Permission',
  'ContactUsRequest',
  'NutritionRecipe',
  'WellnessArticle',
  'Awp',
  'all',
] satisfies Subject[]);

function warnIfDrifted(rule: FrontendRule): void {
  const actions = Array.isArray(rule.action) ? rule.action : [rule.action];
  const subjects = Array.isArray(rule.subject) ? rule.subject : [rule.subject];
  for (const a of actions) {
    if (!KNOWN_ACTIONS.has(a)) {
      console.warn(`fetchMyPermissions: unknown action "${a}" — frontend Action union may be stale (see CNP-151)`);
    }
  }
  for (const s of subjects) {
    if (!KNOWN_SUBJECTS.has(s)) {
      console.warn(`fetchMyPermissions: unknown subject "${s}" — frontend Subject union may be stale (see CNP-151)`);
    }
  }
}

// Returns the raw rules array. The MongoAbility is built lazily inside the
// useAbility() selector so the store stays JSON-serialisable and devtools
// readable — see packages/shared/src/stores/authStore.ts.
//
// Decision: callers store the result in authStore (Zustand), not TanStack
// Query, even though this is API-sourced data. Permissions are strictly bound
// to the auth lifecycle — fetched on login, cleared on logout, never
// independently refetchable — and need to be readable outside React (e.g. by
// graphql-client retry logic). Caching/refetch semantics from TanStack Query
// don't apply here; co-locating with `user` keeps state transitions atomic.
export async function fetchMyPermissions(): Promise<FrontendRule[]> {
  const data = await gqlRequest(GetMyPermissionsDocument);
  // rules is typed as Record<string, unknown> because of the JSON scalar
  // mapping in codegen, but the wire format is FrontendRule[] (see
  // api/src/auth/casl/pack-for-frontend.ts).
  const rules = (data.myPermissions.rules as unknown as FrontendRule[]) ?? [];
  for (const rule of rules) warnIfDrifted(rule);
  return rules;
}
